import { useCallback, useEffect, useState } from 'react';
import Config from 'react-native-config';
import { useDispatch, useSelector } from 'react-redux';
import {
  lowPriorityItemsSelector,
  highPriorityItemsSelector,
  highPriorityItemsIsFetchingSelector,
  lowPriorityItemsIsFetchingSelector
} from 'store/priorityItems/selectors';
import { getLowPriorityItems, getHighPriorityItems } from 'store/priorityItems/actions';
import {
  GetHighPriorityActionItems,
  GetLowPriorityActionItems,
  queryOptions
} from 'services/orchestration/GetActionItems';
import { useQuery } from 'hooks/useQuery';
import { GetHighPriorityActionItems as GetHighPriorityActionItemsQuery } from 'services/orchestration/__generated__/GetHighPriorityActionItems';
import { GetLowPriorityActionItems as GetLowPriorityActionItemsQuery } from 'services/orchestration/__generated__/GetLowPriorityActionItems';
import { useProxySwitcherEffect } from './useProxySwitcherEffect';

/**
 * A useEffect that will get priority items
 */

export const useLowPriorityActionItems = () => {
  const [data, setData] = useState({});
  const [errors, setErrors] = useState([]);
  const [refetch, setRefetch] = useState(() => undefined);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const lowPriorityItems = useSelector(lowPriorityItemsSelector);
  const lowPriorityIsFetching = useSelector(lowPriorityItemsIsFetchingSelector);

  const lowPriorityQueryResult = useQuery<GetLowPriorityActionItemsQuery>(
    GetLowPriorityActionItems,
    {
      ...queryOptions,
      fetchPolicy: 'cache-first',
      skip: !!Config.LOW_PRIORITY_ITEMS_EXPANSION_BASE_URL
    }
  );

  useProxySwitcherEffect(() => {
    if (!Config.LOW_PRIORITY_ITEMS_EXPANSION_BASE_URL) {
      return;
    }
    dispatch(getLowPriorityItems());
  }, []);

  const getExpansionLowPriorityItems = useCallback(() => {
    const {
      data: lowPriorityDataGql,
      error: lowPriorityErrorGql,
      loading: lowPriorityLoadingGql,
      refetch: lowPriorityRefetchGql
    } = lowPriorityQueryResult;

    if (Config.LOW_PRIORITY_ITEMS_EXPANSION_BASE_URL) {
      if (lowPriorityItems) {
        setData(lowPriorityItems[0]?.getActionItems);
        setErrors(lowPriorityItems?.errors || []);
        setLoading(lowPriorityIsFetching);
        setRefetch(() => () => dispatch(getLowPriorityItems(false)));
      }
    } else {
      setData(lowPriorityDataGql?.getActionItems ?? {});
      setErrors(lowPriorityErrorGql?.graphQLErrors || []);
      setLoading(lowPriorityLoadingGql);
      setRefetch(() => lowPriorityRefetchGql);
    }
  }, [lowPriorityQueryResult, lowPriorityItems]);

  useEffect(() => {
    getExpansionLowPriorityItems();
  }, [lowPriorityItems, lowPriorityQueryResult, lowPriorityIsFetching]);

  return {
    lowPriorityData: data,
    lowPriorityErrors: errors,
    lowPriorityLoading: loading,
    lowPriorityRefetch: refetch
  };
};

export const useHighPriorityActionItems = () => {
  const [data, setData] = useState({});
  const [errors, setErrors] = useState([]);
  const [refetch, setRefetch] = useState(() => undefined);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const highPriorityItems = useSelector(highPriorityItemsSelector);
  const highPriorityIsFetching = useSelector(highPriorityItemsIsFetchingSelector);
  const highPriorityQueryResult = useQuery<GetHighPriorityActionItemsQuery>(
    GetHighPriorityActionItems,
    {
      ...queryOptions,
      fetchPolicy: 'cache-first',
      skip: !!Config.HIGH_PRIORITY_ITEMS_EXPANSION_BASE_URL
    }
  );

  useProxySwitcherEffect(() => {
    if (!Config.HIGH_PRIORITY_ITEMS_EXPANSION_BASE_URL) {
      return;
    }
    dispatch(getHighPriorityItems());
  }, []);

  const getExpansionHighPriorityItems = useCallback(() => {
    const {
      data: highPriorityDataGql,
      error: highPriorityErrorGql,
      loading: highPriorityLoadingGql,
      refetch: highPriorityRefetchGql
    } = highPriorityQueryResult;

    if (Config.HIGH_PRIORITY_ITEMS_EXPANSION_BASE_URL) {
      if (highPriorityItems) {
        setData(highPriorityItems[0]?.getActionItems);
        setErrors(highPriorityItems?.errors || []);
        setLoading(highPriorityIsFetching);
        setRefetch(() => () => dispatch(getHighPriorityItems(false)));
      }
    } else {
      setData(highPriorityDataGql?.getActionItems);
      setErrors(highPriorityErrorGql?.graphQLErrors || []);
      setLoading(highPriorityLoadingGql);
      setRefetch(() => highPriorityRefetchGql);
    }
  }, [highPriorityItems, highPriorityQueryResult, highPriorityIsFetching]);

  useEffect(() => {
    getExpansionHighPriorityItems();
  }, [highPriorityItems, highPriorityQueryResult, highPriorityIsFetching]);

  return {
    highPriorityData: data,
    highPriorityErrors: errors,
    highPriorityLoading: loading,
    highPriorityRefetch: refetch
  };
};
